/**
 * LessonOrganizerPanel
 *
 * Right-hand panel with two tabs:
 *   - Lesson: segment cards, each with embedded clips + "Add Recording" / "Side-by-Side" buttons
 *   - Notes: freeform coach notes textarea
 *
 * Segments can be reordered via HTML5 drag-and-drop.
 * Clips are sub-items nested within each parent segment card.
 */

import { FitText } from './FitText';
import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { VoiceoverNotableSegmentClip, NotableSegment } from '../types/videoEdit';
import type { LessonDrillRef, Drill, OfferingType } from '../types/lesson';
import { DrillBrowser } from './DrillBrowser';
import { useFitTier } from './videoEditor/useFitTier';
import { useTouchReorder } from './videoEditor/useTouchReorder';
import { CompareSheet } from './videoEditor/CompareSheet';
import { COMPARE_ICONS, COMPARE_LABELS } from './videoEditor/compareSources';
import { usePhoneLayout } from './videoEditor/usePhoneLayout';
import { placeOnMount, type Box } from './videoEditor/menuPlacement';
import {
  EBTN_ICON_XS,
  EBTN_PRIMARY,
  EBTN_SECONDARY,
  EBTN_GHOST,
  EBTN_SEGMENTED,
  EBTN_DESTRUCTIVE,
} from './videoEditor/editorButtonStyles';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface LessonOrganizerPanelProps {
  clips: VoiceoverNotableSegmentClip[];
  notableSegments: NotableSegment[];
  notes: string;
  onNotesChange: (notes: string) => void;
  /** Private coach-only notes */
  coachNotes?: string;
  onCoachNotesChange?: (notes: string) => void;
  /** Open ClipEditorModal for the given segment */
  onAddRecording: (segmentId: string) => void;
  /** Open side-by-side clip editor with the chosen secondary source */
  onAddSideBySide: (
    segmentId: string,
    sourceType: 'camera' | 'upload' | 'currentVideo' | 'playerLibrary' | 'drillLibrary' | 'proLibrary',
  ) => void;
  /** Whether the player has a library of saved segments (shows "From Previous Lesson" option) */
  hasPlayerLibrary?: boolean;
  onDeleteClip: (clipId: string) => void;
  onDeleteSegment: (segmentId: string) => void;
  onPlaySegment: (seg: NotableSegment) => void;
  onRenameSegment: (segId: string, newLabel: string) => void;
  onReorderSegments: (segments: NotableSegment[]) => void;
  /** Drill snapshots currently attached to this lesson */
  attachedDrills?: LessonDrillRef[];
  /** Toggle a drill on/off */
  onToggleDrill?: (drill: Drill) => void;
  /** Coach ID for fetching drills */
  coachId?: string;
  /** Preview a single clip by ID */
  onPreviewClip?: (clipId: string) => void;
  /** Preview all clips in sequence */
  onPreviewLesson?: () => void;
  /** Rename a clip */
  onRenameClip?: (clipId: string, newLabel: string) => void;
  /** Offering type — drives the ideal-length hint copy. Optional; omitting falls back to the ~16 min default. */
  offeringType?: OfferingType;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ── SideBySidePopover ─────────────────────────────────────────────────────────

interface SideBySidePopoverProps {
  segmentId: string;
  allSegments: NotableSegment[];
  hasPlayerLibrary?: boolean;
  onSelect: (sourceType: 'camera' | 'upload' | 'currentVideo' | 'playerLibrary' | 'drillLibrary' | 'proLibrary') => void;
  onClose: () => void;
  style?: React.CSSProperties;
}

function SideBySidePopover({ segmentId, allSegments, hasPlayerLibrary, onSelect, onClose, style }: SideBySidePopoverProps) {
  /**
   * Flips itself above the button when it does not fit below.
   *
   * The caller used to decide with a hardcoded 180, written when this menu had
   * four rows. Drill Library and Pro Library brought it to six and the real
   * height to ~218, so the check passed and the last rows opened past the
   * bottom of the window. Measuring after mount cannot drift: add a seventh row
   * and this still lands.
   *
   * The comparison is against window.innerHeight because the popover is
   * portalled to document.body and position: fixed — the rail scrolls, but its
   * scroll extent is irrelevant to where a fixed element can be seen.
   */
  const selfRef = useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = selfRef.current;
    const top = typeof style?.top === 'number' ? style.top : null;
    if (!el || top === null) return;
    const h = el.getBoundingClientRect().height;
    const overflow = top + h + 4 - window.innerHeight;
    // 9 = the 4 gap below the button plus the 5 the anchored-above case uses.
    const next = overflow > 0 ? window.innerHeight - top + 9 : null;
    /* Synchronous by necessity: the flip has to land before paint, or the menu
       is visibly drawn in the wrong place first. Guarded so it settles in one
       pass — the second run computes the same value and React bails out. */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFlipped((prev) => (prev === next ? prev : next));
  }, [style?.top, hasPlayerLibrary, allSegments.length]);
  const placement: React.CSSProperties = flipped === null
    ? {}
    : { top: undefined, bottom: flipped };
  // Mirror the Current Video tab: exclude external-source segments and the primary segment itself
  const otherSegments = allSegments.filter(s => s.id !== segmentId && !s.externalSource);
  const hasOtherSegments = otherSegments.length > 0;

  return (
    <div ref={selfRef} className="rounded-lg shadow-lg w-56 overflow-hidden" style={{ ...style, ...placement, background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
      <button
        onClick={() => { onSelect('camera'); onClose(); }}
        className="w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2.5"
      >
        <svg className="w-4 h-4 text-biscayne" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"/>
        </svg>
        Record with Camera
      </button>
      <button
        onClick={() => { onSelect('upload'); onClose(); }}
        className="w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2.5"
      >
        <svg className="w-4 h-4 text-biscayne" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
        </svg>
        Upload from Device
      </button>
      <button
        onClick={hasOtherSegments ? () => { onSelect('currentVideo'); onClose(); } : undefined}
        disabled={!hasOtherSegments}
        className={`w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs flex items-center gap-2.5 ${
          hasOtherSegments ? 'text-[var(--editor-text)] hover:bg-[var(--editor-hover)]' : 'text-[var(--editor-text-muted)] cursor-default'
        }`}
      >
        <svg className={`w-4 h-4 ${hasOtherSegments ? 'text-biscayne' : 'text-[var(--editor-text-muted)]'}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-2.625 0V5.625m0 0A1.125 1.125 0 014.5 4.5h15a1.125 1.125 0 011.125 1.125m-17.25 0v1.5c0 .621.504 1.125 1.125 1.125M19.5 4.5v1.5m0 0a1.125 1.125 0 01-1.125 1.125M19.5 6v1.5m0 9V9.375M6 6.75h.008v.008H6V6.75zm0 3h.008v.008H6V9.75zm0 3h.008v.008H6v-.008zm0 3h.008v.008H6v-.008zm12-9h.008v.008H18V6.75zm0 3h.008v.008H18V9.75zm0 3h.008v.008H18v-.008z"/>
        </svg>
        From This Lesson
      </button>
      <button
        onClick={hasPlayerLibrary ? () => { onSelect('playerLibrary'); onClose(); } : undefined}
        disabled={!hasPlayerLibrary}
        className={`w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs flex items-center gap-2.5 ${
          hasPlayerLibrary ? 'text-[var(--editor-text)] hover:bg-[var(--editor-hover)]' : 'text-[var(--editor-text-muted)] cursor-default'
        }`}
      >
        <svg className={`w-4 h-4 ${hasPlayerLibrary ? 'text-biscayne' : 'text-[var(--editor-text-muted)]'}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        From Previous Lesson
        {!hasPlayerLibrary && <span className="ml-auto text-[10px]">N/A</span>}
      </button>
      {/* The two libraries were reachable only from inside the modal, so the
          rail offered four of the six sources. Same routing as the two rows
          above: open the picker on that source. */}
      <button
        onClick={() => { onSelect('drillLibrary'); onClose(); }}
        className="w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2.5"
      >
        {/* Heroicons academic-cap, outline 24 at strokeWidth 1.5, matching the
            four rows above. */}
        <svg className="w-4 h-4 text-biscayne" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.636 50.636 0 00-2.658-.813A59.906 59.906 0 0112 3.493a59.903 59.903 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
        </svg>
        Drill Library
      </button>
      <button
        onClick={() => { onSelect('proLibrary'); onClose(); }}
        className="w-full px-3 py-2.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2.5"
      >
        {/* Heroicons trophy, same set and weight. */}
        <svg className="w-4 h-4 text-biscayne" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
        </svg>
        Pro Library
      </button>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * A segment card's actions on a phone (spec 10.3): "+ Record" and "Side by
 * side", 32 tall, Subhead 15/20, hugging their labels in one row. Where they
 * do not fit (the landscape panel) they drop to short labels, then to icons
 * alone, measured rather than guessed.
 */
function PhoneSegmentActions({ onRecord, onSideBySide }: { onRecord: () => void; onSideBySide: (rect: DOMRect) => void }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const tier = useFitTier(rowRef, 2);
  const iconOnly = tier >= 2;
  const btn = (fill: string): React.CSSProperties => ({
    height: 32, fontSize: 15, lineHeight: '20px',
    ...(iconOnly ? { width: 32, paddingLeft: 0, paddingRight: 0 } : { paddingLeft: tier === 0 ? 12 : 8, paddingRight: tier === 0 ? 12 : 8 }),
    ...(fill ? { background: fill } : {}),
  });
  return (
    <div ref={rowRef} className="px-3 py-2 flex items-center gap-2 relative overflow-hidden" style={{ borderTop: '1px solid var(--editor-border)' }}>
      <button type="button" data-testid="add-recording-btn" onClick={onRecord} aria-label="Record" title="Record"
        className={`${EBTN_PRIMARY} flex-none`} style={btn('var(--color-biscayne)')}>
        <svg className="w-4 h-4 flex-none" fill="none" stroke="currentColor" strokeWidth="2.25" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
        {!iconOnly && 'Record'}
      </button>
      <button type="button" data-testid="side-by-side-btn" onClick={(e) => onSideBySide(e.currentTarget.getBoundingClientRect())}
        aria-label="Side by side" title="Side by side" className={`${EBTN_SECONDARY} flex-none`} style={btn('')}>
        <svg className="w-4 h-4 flex-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5z"/>
        </svg>
        {!iconOnly && (tier === 0 ? 'Side by side' : 'Compare')}
      </button>
    </div>
  );
}

/**
 * Phone tab labels (spec 10.2), inline so they beat the recipe's 11. Subhead
 * 15/20 wherever the three fit; where they do not (the landscape panel),
 * measured steps down: Footnote 13/18, then Caption 1 12/16 with less
 * padding, then the count badges become dots.
 */
const PHONE_TAB_TIERS: React.CSSProperties[] = [
  { fontSize: 15, lineHeight: '20px' },
  { fontSize: 13, lineHeight: '18px', paddingLeft: 8, paddingRight: 8 },
  { fontSize: 12, lineHeight: '16px', paddingLeft: 4, paddingRight: 4 },
  { fontSize: 12, lineHeight: '16px', paddingLeft: 4, paddingRight: 4 },
];

export function LessonOrganizerPanel({
  clips,
  notableSegments,
  notes,
  onNotesChange,
  coachNotes,
  onCoachNotesChange,
  onAddRecording,
  onAddSideBySide,
  onDeleteClip,
  onDeleteSegment,
  onPlaySegment,
  onRenameSegment,
  onReorderSegments,
  attachedDrills,
  onToggleDrill,
  coachId,
  onPreviewClip,
  onPreviewLesson,
  onRenameClip,
  hasPlayerLibrary,
  offeringType,
}: LessonOrganizerPanelProps) {
  /* Phone: 44 for the row buttons. Their height is inline, so it is set here
     rather than with a max-md class. */
  const phoneSnap = usePhoneLayout();
  const isMobile = phoneSnap.phone;
  /* Spec 14.2: on a phone every menu opens where it fits (menuPlacement.ts),
     placed from its button's rect: below when there is room, above when not,
     always on screen and inside the safe areas. */
  const [segMenuAnchor, setSegMenuAnchor] = useState<Box | null>(null);
  const [clipMenuAnchor, setClipMenuAnchor] = useState<Box | null>(null);
  const rectBox = (r: DOMRect): Box => ({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
  const idealLengthLabel = offeringType === 'shot_consult' ? 'Ideal consult length: 4-8 min' : 'Ideal lesson length: ~16 min';
  const [tab, setTab] = useState<'recordings' | 'drills' | 'notes'>('recordings');
  const tabTrackRef = useRef<HTMLDivElement>(null);
  /* Phone (spec 11.5): reorder the segment cards by touch. */
  const touchReorder = useTouchReorder(notableSegments, onReorderSegments, isMobile);
  const tabTier = useFitTier(tabTrackRef, isMobile ? 3 : 0, `${notableSegments.length}/${attachedDrills?.length ?? 0}/${!!(notes.trim() || coachNotes?.trim())}`);
  const [segMenuId, setSegMenuId] = useState<string | null>(null);
  const [clipMenuId, setClipMenuId] = useState<string | null>(null);
  const [clipMenuPos, setClipMenuPos] = useState({ top: 0, right: 0 });
  const [editingSegId, setEditingSegId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editingClipId, setEditingClipId] = useState<string | null>(null);
  const [editClipLabel, setEditClipLabel] = useState('');
  const [sbsPopoverId, setSbsPopoverId] = useState<string | null>(null);
  const [sbsPopoverPos, setSbsPopoverPos] = useState<{ top?: number; bottom?: number; right: number }>({ right: 0 });
  const [dragSegId, setDragSegId] = useState<string | null>(null);
  const [dragOverSegId, setDragOverSegId] = useState<string | null>(null);
  const [confirmDeleteSegId, setConfirmDeleteSegId] = useState<string | null>(null);
  const [confirmDeleteClipId, setConfirmDeleteClipId] = useState<string | null>(null);

  // Close side-by-side popover on window resize
  useEffect(() => {
    const close = () => setSbsPopoverId(null);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, []);

  const totalRecordedSeconds = clips.reduce((sum, c) => sum + (c.videoDuration ?? 0), 0);

  // ── Drag-to-reorder segments ──────────────────────────────────────────────────
  const handleDragStart = (segId: string) => setDragSegId(segId);
  const handleDragOver = (e: React.DragEvent, segId: string) => { e.preventDefault(); setDragOverSegId(segId); };
  const handleDrop = (targetSegId: string) => {
    if (dragSegId && dragSegId !== targetSegId) {
      const arr = [...notableSegments];
      const fromIdx = arr.findIndex(s => s.id === dragSegId);
      const toIdx = arr.findIndex(s => s.id === targetSegId);
      if (fromIdx !== -1 && toIdx !== -1) {
        const [moved] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, moved);
        onReorderSegments(arr);
      }
    }
    setDragSegId(null);
    setDragOverSegId(null);
  };
  const handleDragEnd = () => { setDragSegId(null); setDragOverSegId(null); };

  const anyMenuOpen = !!(segMenuId || clipMenuId || sbsPopoverId);

  return (
    <div
      /* On a phone, whatever width its container gives it: the full width in
         portrait, the rail in landscape. md:w-80 would otherwise apply to a
         phone on its side. */
      /* Phone (spec 9.2): natural height, so the page around it scrolls as
         one, tabs included. */
      className={isMobile
        ? 'w-full flex flex-col flex-shrink-0'
        : 'w-full md:w-80 rounded-none md:rounded-xl md:border-l md:border-[var(--editor-border)] flex flex-col h-full flex-shrink-0 overflow-hidden'}
      style={{ background: 'var(--editor-sidebar)', boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}
      // borderLeft only on desktop (sidebar mode)
      // Applied via Tailwind: md:border-l md:border-[var(--editor-border)]
    >
      {/* ── Header ──
          Not on a phone: the Lesson Plan bar directly above already names this
          panel, and the two labels stacked cost the plan a row of its height. */}
      <div className="flex items-center px-4 py-3 flex-shrink-0 phone:hidden" style={{ borderBottom: '1px solid var(--editor-border)' }}>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[var(--editor-text-muted)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
          </svg>
          <h3 className="text-sm font-bold text-[var(--editor-text)]">Lesson Organizer</h3>
        </div>
      </div>

      {/* ── Lesson length info ── (on a phone it is the tab bar's trailing text) */}
      {!isMobile && (
      <div className="flex items-center justify-between flex-wrap gap-1 px-4 py-2.5 flex-shrink-0 overflow-hidden" style={{ borderBottom: '1px solid var(--editor-border)' }}>
        <div className="flex items-center gap-1.5 min-w-0">
          <svg className="w-3.5 h-3.5 text-[var(--editor-text-muted)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
          </svg>
          <span className="min-w-0 text-[11px] text-[var(--editor-text-muted)]">{idealLengthLabel}</span>
        </div>
        <div className="flex flex-col items-end flex-shrink-0">
          <span className="text-[11px] text-[var(--editor-text-muted)] font-normal">Recorded</span>
          <span className="text-[11px] font-semibold font-mono text-[var(--color-db-oasis)]">{formatTime(totalRecordedSeconds)}</span>
        </div>
      </div>
      )}

      {/* ── Tabs ──
          A segmented control rather than an underline bar. Same three options,
          same handlers, same badges; only the treatment changes. The track is
          full width with flex-1 items so the thirds are kept, and at 32 tall
          inside a 36 row nothing grows. */}
      {/* Phone (spec 10.2): the tabs on their own full-width 36 row, 8 in
          from each side, labels Subhead 15/20; the recorded time is a grey
          Caption 1 line under them. */}
      <div className={isMobile ? 'flex flex-col px-2 pt-2 pb-1.5 flex-shrink-0' : 'px-2 py-1 flex-shrink-0'} style={{ borderBottom: '1px solid var(--editor-border)' }}>
      <div ref={tabTrackRef} role="radiogroup" aria-label="Organizer section" className={isMobile ? `${EBTN_SEGMENTED.track} w-full min-w-0 h-9 overflow-hidden` : `${EBTN_SEGMENTED.track} w-full phone:h-11`}>
        <button
          type="button"
          role="radio"
          aria-checked={tab === 'recordings'}
          onClick={() => setTab('recordings')}
          className={`${tab === 'recordings' ? EBTN_SEGMENTED.itemSelected : EBTN_SEGMENTED.item} flex-1`}
          style={isMobile ? PHONE_TAB_TIERS[tabTier] : undefined}
        >
          Lesson
          {notableSegments.length > 0 && (
            isMobile && tabTier >= 3
              ? <span className="flex-none inline-block w-[6px] h-[6px] rounded-full bg-[var(--color-biscayne)]" aria-label={`${notableSegments.length}`} />
              : <span className={`${isMobile ? '' : 'ml-1.5 '}flex-none inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--editor-timeline)] text-[var(--color-biscayne)] text-[10px] font-bold`}>
              {notableSegments.length}
            </span>
          )}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={tab === 'drills'}
          onClick={() => setTab('drills')}
          className={`${tab === 'drills' ? EBTN_SEGMENTED.itemSelected : EBTN_SEGMENTED.item} flex-1`}
          style={isMobile ? PHONE_TAB_TIERS[tabTier] : undefined}
        >
          Drills
          {(attachedDrills?.length ?? 0) > 0 && (
            isMobile && tabTier >= 3
              ? <span className="flex-none inline-block w-[6px] h-[6px] rounded-full bg-[var(--color-biscayne)]" aria-label={`${attachedDrills!.length}`} />
              : <span className={`${isMobile ? '' : 'ml-1.5 '}flex-none inline-flex items-center justify-center w-4 h-4 rounded-full bg-[var(--editor-timeline)] text-[var(--color-biscayne)] text-[10px] font-bold`}>
              {attachedDrills!.length}
            </span>
          )}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={tab === 'notes'}
          onClick={() => setTab('notes')}
          className={`${tab === 'notes' ? EBTN_SEGMENTED.itemSelected : EBTN_SEGMENTED.item} flex-1`}
          style={isMobile ? PHONE_TAB_TIERS[tabTier] : undefined}
        >
          Notes
          {(notes.trim() || coachNotes?.trim()) && <span className={`${isMobile ? 'flex-none' : 'ml-1'} inline-block w-[6px] h-[6px] rounded-full bg-[var(--color-db-oasis)] align-middle`}/>}
        </button>
      </div>
      {isMobile && (
        <span className="block px-2 pt-1.5 text-[12px] leading-[16px]" style={{ color: 'var(--editor-text-muted)' }} data-testid="organizer-recorded-line">
          Recorded {formatTime(totalRecordedSeconds)}{offeringType === 'shot_consult' ? ' of 4-8 min' : ' of ~16 min'}
        </span>
      )}
      </div>

      {/* ── Content ── */}
      <div className={isMobile ? '' : "flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[var(--editor-sidebar)] [&::-webkit-scrollbar-thumb]:bg-[var(--editor-border)] [&::-webkit-scrollbar-thumb]:rounded-sm"}>

        {/* ── Recordings tab ── */}
        {tab === 'recordings' && (
          <div className={isMobile ? 'flex flex-col' : 'flex flex-col h-full'}>
            {/* Preview Lesson button — only when there are recordable clips */}
            {clips.some((c) => c.videoBlob || c.videoStoragePath) && onPreviewLesson && isMobile && (
              /* Phone (spec 9.2): a 44 row at the top of the list. */
              <button
                type="button"
                onClick={onPreviewLesson}
                className="w-full flex items-center gap-2 px-3 text-left text-[15px] leading-[20px] ios-press"
                style={{ height: 44, color: 'var(--editor-text)', borderBottom: '1px solid var(--editor-border)' }}
              >
                <svg className="w-4 h-4 flex-none" style={{ color: 'var(--color-biscayne)' }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                Preview lesson
              </button>
            )}
            {clips.some((c) => c.videoBlob || c.videoStoragePath) && onPreviewLesson && !isMobile && (
              <div className="px-2 pt-2 flex-shrink-0">
                <button
                  onClick={onPreviewLesson}
                  /* EBTN_GHOST, not SECONDARY: this is an optional aside above
                     the segment list, and a filled chip there competes with the
                     Add Recording buttons below it. The dashed border went with
                     it — the recipe has no dashed variant, and a ghost already
                     reads as the lighter of the two. */
                  className={`${EBTN_GHOST} w-full`}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  Preview Lesson
                </button>
              </div>
            )}
            {notableSegments.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--editor-timeline)] flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-[var(--editor-text-muted)]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-[var(--editor-text)] mb-1">No segments yet</p>
                <p className="text-xs text-[var(--editor-text-muted)] leading-relaxed max-w-[200px]">
                  Use the flag tool on the timeline to highlight moments you want to coach
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-2" data-reorder-list>
                <p className="text-[11px] text-[var(--editor-text-muted)] px-1">
                  {notableSegments.length} segment{notableSegments.length !== 1 ? 's' : ''}
                </p>

                {notableSegments.map(seg => {
                  const segClips = clips.filter(c => c.segmentIds[0] === seg.id);
                  const isEditing = editingSegId === seg.id;
                  const isDragOver = dragOverSegId === seg.id && dragSegId !== seg.id;

                  return (
                    <div
                      key={seg.id}
                      data-reorder-id={seg.id}
                      draggable={!isMobile}
                      onDragStart={() => handleDragStart(seg.id)}
                      onDragOver={(e) => handleDragOver(e, seg.id)}
                      onDrop={() => handleDrop(seg.id)}
                      onDragEnd={handleDragEnd}
                      className={`rounded-lg border bg-[var(--editor-surface)] overflow-hidden transition-all hover:bg-[var(--editor-hover)] ${
                        isDragOver ? 'border-db-accent shadow-md' : 'border-[var(--editor-border)]'
                      } ${dragSegId === seg.id ? 'opacity-50' : ''}`}
                      style={{ borderLeft: segClips.length > 0 ? '3px solid var(--color-biscayne)' : '3px solid rgba(255, 255, 255, 0.12)', ...(isMobile ? touchReorder.cardStyle(seg.id) : {}) }}
                    >
                      {/* Card header (phone: a 44 row, 12 padding; a two-line name keeps 6 above and below) */}
                      <div className={isMobile ? 'px-3 py-1.5 min-h-[44px] flex items-center gap-2' : 'px-3 py-2.5 flex items-start gap-2'}>
                        {/* Drag handle. Phone (spec 11.5): long press, drag, drop;
                            a 44 target around the grip. */}
                        <div
                          className={isMobile
                            ? "relative flex-shrink-0 w-6 h-8 -ml-1 flex items-center justify-center text-[var(--editor-text-muted)] after:absolute after:content-[''] after:-inset-x-2.5 after:-inset-y-1.5"
                            : 'flex-shrink-0 mt-0.5 cursor-grab active:cursor-grabbing text-[var(--editor-text-muted)] hover:text-[var(--editor-text)]'}
                          aria-label={isMobile ? `Reorder ${seg.label}. Press and hold, then drag.` : undefined}
                          data-testid={isMobile ? 'segment-drag-handle' : undefined}
                          {...(isMobile ? touchReorder.handleProps(seg.id) : {})}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 6a2 2 0 110-4 2 2 0 010 4zM8 14a2 2 0 110-4 2 2 0 010 4zM8 22a2 2 0 110-4 2 2 0 010 4zM16 6a2 2 0 110-4 2 2 0 010 4zM16 14a2 2 0 110-4 2 2 0 010 4zM16 22a2 2 0 110-4 2 2 0 010 4z"/>
                          </svg>
                        </div>

                        {/* Label */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editLabel}
                              onChange={e => setEditLabel(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && editLabel.trim()) {
                                  onRenameSegment(seg.id, editLabel.trim());
                                  setEditingSegId(null);
                                }
                                if (e.key === 'Escape') setEditingSegId(null);
                              }}
                              onBlur={() => {
                                if (editLabel.trim()) onRenameSegment(seg.id, editLabel.trim());
                                setEditingSegId(null);
                              }}
                              className="w-full px-2 py-0.5 text-[16px] leading-[21px] border border-db-accent rounded focus:outline-none focus:ring-1 focus:ring-db-accent/50 input-glow"
                              style={{ background: 'var(--editor-surface)', color: 'var(--editor-text)' }}
                              autoFocus
                            />
                          ) : (
                            // The segment's whole name: two lines, then a measured step down.
                            <FitText as="p" lines={2} className="text-xs font-semibold text-[var(--editor-text)]">{seg.label}</FitText>
                          )}
                        </div>

                        {/* Time range */}
                        <span className="text-[10px] text-[var(--editor-text-muted)] font-mono flex-shrink-0 mt-0.5">
                          {seg.startTime === seg.endTime
                            ? formatTime(seg.startTime)
                            : `${formatTime(seg.startTime)}–${formatTime(seg.endTime)}`}
                        </span>

                        {/* Three-dot menu */}
                        <div className="relative flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              if (isMobile) setSegMenuAnchor(rectBox(e.currentTarget.getBoundingClientRect()));
                              setSegMenuId(segMenuId === seg.id ? null : seg.id);
                            }}
                            className={EBTN_ICON_XS}
                            aria-label="More options"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                            </svg>
                          </button>
                          {segMenuId === seg.id && (() => {
                            const items = (<>
                              <button
                                onClick={() => { setSegMenuId(null); onPlaySegment(seg); }}
                                className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2"
                              >
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                Play Segment
                              </button>
                              <button
                                onClick={() => { setSegMenuId(null); setEditLabel(seg.label); setEditingSegId(seg.id); }}
                                className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                Rename
                              </button>
                              <button
                                onClick={() => { setSegMenuId(null); setConfirmDeleteSegId(seg.id); }}
                                className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-red-400 hover:bg-red-900/20 flex items-center gap-2"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                Delete Segment
                              </button>
                            </>);
                            return isMobile
                              ? createPortal(
                                <div ref={placeOnMount(segMenuAnchor, phoneSnap.insets)} className="rounded-lg shadow-lg py-1 w-40" style={{ position: 'fixed', zIndex: 'var(--z-editor-menu)', background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>{items}</div>,
                                document.body,
                              )
                              : <div className="absolute right-0 top-7 z-40 rounded-lg shadow-lg py-1 w-40" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>{items}</div>;
                          })()}
                        </div>
                      </div>

                      {/* Clips area */}
                      <div style={{ borderTop: '1px solid var(--editor-border)' }}>
                        {segClips.length === 0 ? (
                          <div className={isMobile ? 'px-3 min-h-[44px] flex items-center' : 'px-3 py-2'}>
                            <p className="text-xs text-[var(--editor-text-muted)] italic">No recorded clips yet</p>
                          </div>
                        ) : (
                          <div>
                            {segClips.map((clip, ci) => {
                              const label = clip.label || (clip.isCameraSource ? 'Camera Recording' : `Recording ${ci + 1}`);
                              const isEditingThisClip = editingClipId === clip.id;
                              return (
                                <div
                                  key={clip.id}
                                  /* Phone (spec 11): the row shows a play icon, so a
                                     tap on it plays the recording (the preview); the
                                     menu still has Preview, Edit Name and Delete. */
                                  {...(isMobile && !isEditingThisClip && onPreviewClip ? { role: 'button', tabIndex: 0, onClick: () => onPreviewClip(clip.id), 'data-testid': 'recording-row' } : {})}
                                  className={`flex items-center gap-2 px-3 ${isMobile ? 'min-h-[44px] ios-press' : 'py-2'} hover:bg-[var(--editor-hover)] transition-colors ${ci > 0 ? 'border-t border-[var(--editor-border)]/50' : ''}`}
                                >
                                  <svg className="w-3.5 h-3.5 text-db-accent flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                  {isEditingThisClip ? (
                                    <input
                                      type="text"
                                      value={editClipLabel}
                                      onChange={e => setEditClipLabel(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' && editClipLabel.trim()) {
                                          onRenameClip?.(clip.id, editClipLabel.trim());
                                          setEditingClipId(null);
                                        }
                                        if (e.key === 'Escape') setEditingClipId(null);
                                      }}
                                      onBlur={() => {
                                        if (editClipLabel.trim()) onRenameClip?.(clip.id, editClipLabel.trim());
                                        setEditingClipId(null);
                                      }}
                                      className="flex-1 min-w-0 px-2 py-0.5 text-[16px] leading-[21px] border border-db-accent rounded focus:outline-none focus:ring-1 focus:ring-db-accent/50 input-glow"
                                      style={{ background: 'var(--editor-surface)', color: 'var(--editor-text)' }}
                                      autoFocus
                                    />
                                  ) : (
                                    <FitText as="p" lines={2} className="text-xs font-medium text-[var(--editor-text)] flex-1">{label}</FitText>
                                  )}
                                  {!isEditingThisClip && clip.videoDuration != null && (
                                    <span className="text-[10px] text-[var(--editor-text-muted)] font-mono flex-shrink-0">
                                      {formatTime(clip.videoDuration)}
                                    </span>
                                  )}
                                  {/* Clip three-dot */}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const r = e.currentTarget.getBoundingClientRect();
                                      setClipMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                                      if (isMobile) setClipMenuAnchor(rectBox(r));
                                      setClipMenuId(clipMenuId === clip.id ? null : clip.id);
                                    }}
                                    className={EBTN_ICON_XS}
                                    aria-label="More options"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                                    </svg>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      {isMobile ? (
                        <PhoneSegmentActions
                          onRecord={() => onAddRecording(seg.id)}
                          onSideBySide={(rect) => {
                            setSbsPopoverPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            setSbsPopoverId(sbsPopoverId === seg.id ? null : seg.id);
                          }}
                        />
                      ) : (
                      <div className={isMobile ? 'px-3 py-2 flex gap-2 relative' : 'px-3 pb-2.5 pt-1.5 flex gap-2 relative'} style={{ borderTop: '1px solid var(--editor-border)' }}>
                        <button
                          type="button"
                          data-testid="add-recording-btn"
                          onClick={() => onAddRecording(seg.id)}
                          /* Radius 100, the shape every other text button in
                             the editor uses. Nothing else changes: the buttons
                             are flex-1 with no horizontal padding of their own,
                             so the pill costs no width and neither label moves. */
                          className={isMobile
                            ? "flex-1 flex items-center justify-center gap-1.5 rounded-[100px] text-[13px] font-semibold text-white text-center leading-[16px] transition-colors ios-press relative after:absolute after:content-[''] after:inset-x-0 after:-inset-y-1"
                            : 'flex-1 flex items-center justify-center gap-1.5 rounded-[100px] text-[13px] font-semibold text-white text-center leading-[16px] transition-colors btn-press'}
                          style={{ minHeight: '36px', background: 'var(--color-biscayne)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-db-accent-hover)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-biscayne)'; }}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                          </svg>
                          Add Recording
                        </button>
                        <button
                          onClick={(e) => {
                            /* Always offered below; SideBySidePopover measures
                               itself after mount and flips above if it does not
                               fit. No height is guessed here any more. */
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSbsPopoverPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                            setSbsPopoverId(sbsPopoverId === seg.id ? null : seg.id);
                          }}
                          className={isMobile
                            ? "flex-1 flex items-center justify-center gap-1 rounded-[100px] text-[13px] font-semibold text-center leading-[16px] transition-colors ios-press relative after:absolute after:content-[''] after:inset-x-0 after:-inset-y-1"
                            : 'flex-1 flex items-center justify-center gap-1 rounded-[100px] text-[13px] font-semibold text-center leading-[16px] transition-colors btn-press'}
                          style={{ minHeight: '36px', background: 'var(--color-db-navy-light)', color: 'var(--color-biscayne)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--editor-drop-active)'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-db-navy-light)'; }}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75v10.5A2.25 2.25 0 004.5 19.5z"/>
                          </svg>
                          Side-by-Side
                        </button>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Notes tab ── */}
        {tab === 'notes' && (
          <div className={isMobile ? 'flex flex-col p-3 gap-3' : 'flex flex-col h-full p-4 gap-3 overflow-y-auto'}>
            {/* Player-facing notes */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-xs font-semibold text-[var(--editor-text)]">Player Notes</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded text-[var(--color-db-oasis)] font-medium" style={{ background: 'rgba(84,195,234,0.12)' }}>Visible to player</span>
              </div>
              <textarea
                value={notes}
                onChange={e => onNotesChange(e.target.value)}
                placeholder="Add drills, key takeaways, or practice reminders…"
                rows={5}
                className="w-full px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-db-accent focus:border-transparent resize-none text-[16px] leading-relaxed text-[var(--editor-text)] placeholder:text-[var(--editor-text-muted)] input-glow"
                style={{ background: 'var(--editor-surface)', border: '1px solid var(--editor-border)' }}
              />
            </div>
            {/* Coach-only notes */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-xs font-semibold text-[var(--editor-text)]">Coach Notes</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded text-[var(--editor-text-muted)] font-medium" style={{ background: 'rgba(136,153,170,0.12)', border: '1px solid rgba(136,153,170,0.2)' }}>Private</span>
              </div>
              <textarea
                value={coachNotes ?? ''}
                onChange={e => onCoachNotesChange?.(e.target.value)}
                placeholder="Internal reminders, follow-up items, notes for yourself…"
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-db-accent focus:border-transparent resize-none text-[16px] leading-relaxed text-[var(--editor-text)] placeholder:text-[var(--editor-text-muted)] input-glow"
                style={{ background: 'var(--editor-surface)', border: '1px solid var(--editor-border)' }}
              />
            </div>
          </div>
        )}

        {/* ── Drills tab ── */}
        {tab === 'drills' && coachId && onToggleDrill && (
          /* Phone (spec 15.25): 16 outer padding round the drills grid. */
          <div className={isMobile ? 'p-4' : 'p-3'}>
            <DrillBrowser
              coachId={coachId}
              selectedDrillIds={attachedDrills?.map((d) => d.drillId) ?? []}
              onToggleDrill={onToggleDrill}
              inLessonEditor
            />
          </div>
        )}
      </div>

      {/* ── Close menus overlay ── */}
      {anyMenuOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => { setSegMenuId(null); setClipMenuId(null); setSbsPopoverId(null); }}
        />
      )}

      {/* ── Side by side on a phone: the "Compare with" sheet (spec 10.11) ── */}
      {sbsPopoverId && isMobile && createPortal(
        (() => {
          const segId = sbsPopoverId;
          const hasOther = notableSegments.some((x) => x.id !== segId && !x.externalSource);
          const pick = (type: Parameters<typeof onAddSideBySide>[1]) => () => { onAddSideBySide(segId, type); setSbsPopoverId(null); };
          return (
            <CompareSheet
              onClose={() => setSbsPopoverId(null)}
              rows={[
                { key: 'camera', label: COMPARE_LABELS.camera, icon: COMPARE_ICONS.camera, available: true, onPick: pick('camera') },
                { key: 'upload', label: COMPARE_LABELS.upload, icon: COMPARE_ICONS.upload, available: true, onPick: pick('upload') },
                { key: 'current', label: COMPARE_LABELS.thisLesson, icon: COMPARE_ICONS.thisLesson, available: hasOther, note: 'None', onPick: pick('currentVideo') },
                { key: 'player', label: COMPARE_LABELS.previousLesson, icon: COMPARE_ICONS.previousLesson, available: !!hasPlayerLibrary, note: 'N/A', onPick: pick('playerLibrary') },
                { key: 'drills', label: COMPARE_LABELS.drillLibrary, icon: COMPARE_ICONS.drillLibrary, available: true, onPick: pick('drillLibrary') },
                { key: 'pro', label: COMPARE_LABELS.proLibrary, icon: COMPARE_ICONS.proLibrary, available: true, onPick: pick('proLibrary') },
              ]}
            />
          );
        })(),
        document.body,
      )}

      {/* ── Side-by-side popover — portaled ── */}
      {sbsPopoverId && !isMobile && createPortal(
        <SideBySidePopover
          segmentId={sbsPopoverId}
          allSegments={notableSegments}
          hasPlayerLibrary={hasPlayerLibrary}
          style={{ position: 'fixed', ...('top' in sbsPopoverPos && sbsPopoverPos.top !== undefined ? { top: sbsPopoverPos.top } : { bottom: sbsPopoverPos.bottom }), right: sbsPopoverPos.right, zIndex: 'var(--z-editor-menu)' }}
          onSelect={(type) => {
            onAddSideBySide(sbsPopoverId, type);
            setSbsPopoverId(null);
          }}
          onClose={() => setSbsPopoverId(null)}
        />,
        document.body,
      )}

      {/* ── Delete confirmation dialog — portaled ── */}
      {(confirmDeleteSegId || confirmDeleteClipId) && createPortal(
        <div className="fixed inset-0 flex items-center justify-center bg-black/70" style={{ zIndex: 'var(--z-editor-dialog)' }}>
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}>
            {confirmDeleteSegId && (() => {
              const seg = notableSegments.find(s => s.id === confirmDeleteSegId);
              const clipCount = clips.filter(c => c.segmentIds[0] === confirmDeleteSegId).length;
              return (
                <>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Delete highlight?</h3>
                  <p className="mb-6 text-sm" style={{ color: 'var(--editor-text-muted)' }}>
                    {clipCount > 0
                      ? `"${seg?.label}" is referenced by ${clipCount} recording${clipCount > 1 ? 's' : ''}. Deleting it will also delete those recordings.`
                      : `Are you sure you want to delete "${seg?.label}"?`}
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={() => setConfirmDeleteSegId(null)} className={EBTN_SECONDARY}>Cancel</button>
                    <button onClick={() => { onDeleteSegment(confirmDeleteSegId); setConfirmDeleteSegId(null); }} className={EBTN_DESTRUCTIVE}>
                      {clipCount > 0 ? 'Delete highlight & recordings' : 'Delete highlight'}
                    </button>
                  </div>
                </>
              );
            })()}
            {confirmDeleteClipId && (() => {
              const clip = clips.find(c => c.id === confirmDeleteClipId);
              const segClips = clips.filter(c => c.segmentIds[0] === clip?.segmentIds[0]);
              const ci = segClips.findIndex(c => c.id === confirmDeleteClipId);
              const label = clip?.label || (clip?.isCameraSource ? 'Camera Recording' : `Recording ${ci + 1}`);
              return (
                <>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--editor-text)' }}>Delete recording?</h3>
                  <p className="mb-6 text-sm" style={{ color: 'var(--editor-text-muted)' }}>Are you sure you want to delete "{label}"?</p>
                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={() => setConfirmDeleteClipId(null)} className={EBTN_SECONDARY}>Cancel</button>
                    <button type="button" onClick={() => { onDeleteClip(confirmDeleteClipId); setConfirmDeleteClipId(null); }} className={EBTN_DESTRUCTIVE}>Delete recording</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body,
      )}

      {/* ── Clip three-dot menu — portaled ── */}
      {clipMenuId && createPortal(
        <div
          ref={isMobile ? placeOnMount(clipMenuAnchor, phoneSnap.insets) : undefined}
          style={{ position: 'fixed', top: clipMenuPos.top, right: clipMenuPos.right, zIndex: 'var(--z-editor-menu)', background: 'var(--editor-sidebar)', border: '1px solid var(--editor-border)' }}
          className="rounded-lg shadow-lg py-1 w-32"
        >
          <button
            className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2"
            onClick={() => { const id = clipMenuId; setClipMenuId(null); if (id) onPreviewClip?.(id); }}
          >
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
            Preview
          </button>
          <button
            className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-[var(--editor-text)] hover:bg-[var(--editor-hover)] flex items-center gap-2"
            onClick={() => {
              const id = clipMenuId;
              setClipMenuId(null);
              if (!id) return;
              const clip = clips.find(c => c.id === id);
              setEditClipLabel(clip?.label || '');
              setEditingClipId(id);
            }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
            Edit Name
          </button>
          <button
            onClick={() => { const id = clipMenuId; setClipMenuId(null); setConfirmDeleteClipId(id); }}
            className="w-full px-3 py-1.5 phone:min-h-[44px] text-left text-xs text-red-400 hover:bg-red-900/20 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            Delete
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
